import { useState, useEffect, useCallback, useRef } from 'react';
import type { PublicRecommendation, PublicRecommendationsListParams, SavedRecommendation, PublicRecommendationWithAssets, ReferenceWithCount, FunnelEvent, RecentlyViewedEntry, RecommendedForYouResult } from '../types';

export function useRecommendations(params: PublicRecommendationsListParams = {}) {
  const [recommendations, setRecommendations] = useState<PublicRecommendation[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetchRecommendations = useCallback(async (fetchParams: PublicRecommendationsListParams, append = false) => {
    try {
      const data = await window.discoveryApi.getRecommendations(fetchParams);
      setRecommendations(prev => append ? [...prev, ...data.recommendations] : data.recommendations);
      setNextCursor(data.nextCursor);
      setHasMore(data.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load recommendations');
      setRecommendations(prev => append ? prev : []);
    } finally {
      setLoading(false);
      setLoadMoreLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setNextCursor(null);
    setHasMore(false);
    fetchRecommendations(paramsRef.current);
  }, [params.q, params.categoryId, params.cityId, fetchRecommendations]);

  const loadMore = useCallback(() => {
    if (!nextCursor || !hasMore || loadMoreLoading) return;
    setLoadMoreLoading(true);
    fetchRecommendations({ ...paramsRef.current, cursor: nextCursor }, true);
  }, [nextCursor, hasMore, loadMoreLoading, fetchRecommendations]);

  const refetch = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchRecommendations(paramsRef.current);
  }, [fetchRecommendations]);

  return { recommendations, loading, loadMoreLoading, error, hasMore, nextCursor, loadMore, refetch };
}

export function useRecommendation(id: string) {
  const [recommendation, setRecommendation] = useState<PublicRecommendationWithAssets | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      try {
        const data = await window.discoveryApi.getRecommendation(id);
        if (mounted) setRecommendation(data);
      } catch (err) {
        if (mounted) setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, [id]);

  return { recommendation, loading, error };
}

export function useReferences() {
  const [categories, setCategories] = useState<ReferenceWithCount[]>([]);
  const [cities, setCities] = useState<ReferenceWithCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      try {
        const [cats, cityRows] = await Promise.all([
          window.discoveryApi.getCategories(),
          window.discoveryApi.getCities(),
        ]);
        if (!mounted) return;
        setCategories(cats);
        setCities(cityRows);
      } catch (err) {
        if (mounted) setError(err instanceof Error ? err.message : 'Failed to load filters');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, []);

  return { categories, cities, loading, error };
}

export function useSavedRecommendations() {
  const [saved, setSaved] = useState<SavedRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    try {
      const data = await window.discoveryApi.getSavedRecommendations();
      setSaved(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const save = useCallback(async (recommendationId: string) => {
    await window.discoveryApi.saveRecommendation(recommendationId);
    await fetch();
  }, [fetch]);

  const unsave = useCallback(async (recommendationId: string) => {
    await window.discoveryApi.unsaveRecommendation(recommendationId);
    setSaved(prev => prev.filter(s => s.recommendationId !== recommendationId));
  }, []);

  return { saved, loading, error, save, unsave, refetch: fetch };
}

/** Only query the private saved-status endpoint when a session token exists. */
export function useSavedStatus(
  recommendationId: string,
  enabled = typeof window !== 'undefined' && Boolean(window.discoveryApi?.getToken?.()),
) {
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setSaved(false);
      setLoading(false);
      setError(null);
      return;
    }

    let mounted = true;
    setLoading(true);
    const check = async () => {
      try {
        const list = await window.discoveryApi.getSavedRecommendations();
        if (!mounted) return;
        setSaved(list.some(s => s.recommendationId === recommendationId));
      } catch (err) {
        if (mounted) setError(err instanceof Error ? err.message : null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    check();
    return () => { mounted = false; };
  }, [recommendationId, enabled]);

  return { saved, loading, error, setSaved };
}

export function useRecentlyViewed(limit = 12) {
  const [items, setItems] = useState<RecentlyViewedEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      try {
        const data = await window.discoveryApi.getRecentlyViewed(limit);
        if (mounted) setItems(data);
      } catch (err) {
        if (mounted) setError(err instanceof Error ? err.message : 'Failed to load recently viewed');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, [limit]);

  return { items, loading, error };
}

export function useRecommendedForYou(limit = 12) {
  const [result, setResult] = useState<RecommendedForYouResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      try {
        const data = await window.discoveryApi.getRecommendedForYou(limit);
        if (mounted) setResult(data);
      } catch (err) {
        if (mounted) setError(err instanceof Error ? err.message : 'Failed to load recommendations');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, [limit]);

  return { result, loading, error };
}

export function useTrackEvent() {
  const track = useCallback((event: {
    type: 'view' | 'click';
    recommendationId: string;
    metadata?: Record<string, unknown>;
  }) => {
    Promise.resolve(window.discoveryApi.trackEvent?.(event)).catch(() => undefined);
  }, []);

  return { track };
}

export function useTrackFunnelEvent() {
  const track = useCallback((event: Omit<FunnelEvent, 'timestamp'>) => {
    Promise.resolve(window.discoveryApi.trackFunnelEvent?.(event)).catch(() => undefined);
  }, []);

  return { track };
}
